import type {
  GeoBreakdownLevel,
  PlotBreakdownLevels,
  TaxonomyBreakdownLevel,
} from '@revelio/core/global-ai-assistant/breakdown-levels';
import { EntKind } from '@revelio/data-access';

import { getEntKindForGeoOrRicsLevelId } from '../role-skill-ent-kind';
import type { CompositionV3BreakdownLevelOverrides } from './getCompositionV3BreakdownOptions';

const GEO_LEVEL_TO_ENT_KIND: Record<GeoBreakdownLevel, EntKind> = {
  region: EntKind.Region,
  country: EntKind.Country,
  state: EntKind.State,
  metro: EntKind.Msa,
  county: EntKind.County,
  city: EntKind.City,
};

/**
 * How deep each wire level sits in a geography taxonomy. Both taxonomies run
 * region, country, then their own two finer levels (state and county, or
 * metro and city), so a depth names the same rung in either.
 */
const GEO_LEVEL_DEPTH: Record<GeoBreakdownLevel, number> = {
  region: 0,
  country: 1,
  state: 2,
  metro: 2,
  county: 3,
  city: 3,
};

/**
 * Translate a plot's wire-format breakdown levels into the composition-v3
 * override shape. Absent/empty input returns undefined so the drill-down
 * default (`geoBreakdownLevelFromFilters`) keeps deciding — this function
 * must be a pure no-op for every chart that doesn't carry the field.
 *
 * The level is placed on the ACTIVE taxonomy's ladder: a level that ladder
 * lacks ("state" on Metro Area & City) becomes the rung at the same depth
 * (metro), so the request never asks for places the taxonomy cannot return.
 */
export const plotBreakdownLevelsToOverrides = (
  breakdownLevels: PlotBreakdownLevels | undefined,
  geographySelectionListIds: readonly string[]
): CompositionV3BreakdownLevelOverrides | undefined => {
  const geography = breakdownLevels?.geography;
  const taxonomyOverrides: CompositionV3BreakdownLevelOverrides = {};
  const roleLevels: Record<number, EntKind> = {
    1: EntKind.RoleLvl_1,
    3: EntKind.RoleLvl_3,
    5: EntKind.RoleLvl_5,
    7: EntKind.RoleLvl_7,
  } satisfies Record<TaxonomyBreakdownLevel<'role'>, EntKind>;
  const skillLevels: Record<number, EntKind> = {
    3: EntKind.SkillLvl_3,
    5: EntKind.SkillLvl_5,
    6: EntKind.SkillLvl_6,
    7: EntKind.SkillLvl_7,
    8: EntKind.SkillLvl_8,
  } satisfies Record<TaxonomyBreakdownLevel<'skills'>, EntKind>;
  const ricsLevels: Record<number, EntKind> = {
    10: EntKind.RicsK10,
    50: EntKind.RicsK50,
    400: EntKind.RicsK400,
  } satisfies Record<TaxonomyBreakdownLevel<'rics'>, EntKind>;
  if (breakdownLevels?.role !== undefined && roleLevels[breakdownLevels.role])
    taxonomyOverrides.role_breakdown_level = roleLevels[breakdownLevels.role];
  if (
    breakdownLevels?.skills !== undefined &&
    skillLevels[breakdownLevels.skills]
  )
    taxonomyOverrides.skill_breakdown_level =
      skillLevels[breakdownLevels.skills];
  if (breakdownLevels?.rics !== undefined && ricsLevels[breakdownLevels.rics])
    taxonomyOverrides.rics_breakdown_level = ricsLevels[breakdownLevels.rics];
  if (geography === undefined)
    return Object.keys(taxonomyOverrides).length
      ? taxonomyOverrides
      : undefined;
  const ladder = geographySelectionListIds
    .map((levelId) => getEntKindForGeoOrRicsLevelId(levelId))
    .filter((entKind): entKind is EntKind => entKind !== null);
  const asked = GEO_LEVEL_TO_ENT_KIND[geography];
  const geo_breakdown_level = ladder.includes(asked)
    ? asked
    : (ladder[GEO_LEVEL_DEPTH[geography]] ?? asked);
  return { ...taxonomyOverrides, geo_breakdown_level };
};
